import ExcelJS from 'exceljs';

const HEADERS = ['학기', '날짜', '요일', '교시', '프로그램', '단원', '학습내용', '구분', '메모'] as const;
const COLUMN_COUNT = HEADERS.length;
const HEADER_ROW_INDEX = 2;
const DATA_START_ROW_INDEX = 3;

export type ClassRecordMemosExportRow = {
  termLabel: string;
  dateLabel: string;
  weekdayLabel: string;
  periodLabel: string;
  programLabel: string;
  unitLabel: string;
  learningLabel: string;
  kindLabel: string;
  note: string;
};

function solidCellBorder(): Partial<ExcelJS.Borders> {
  const side: ExcelJS.Border = { style: 'thin', color: { argb: 'FF000000' } };
  return {
    top: { ...side },
    left: { ...side },
    bottom: { ...side },
    right: { ...side },
  };
}

const HEADER_FILL: ExcelJS.Fill = {
  type: 'pattern',
  pattern: 'solid',
  fgColor: { argb: 'FFF4F4F4' },
};

function displayWidthOfText(text: string): number {
  let width = 0;
  for (const ch of text) {
    width += ch.charCodeAt(0) > 255 ? 2 : 1;
  }
  return width;
}

function rowToCells(row: ClassRecordMemosExportRow): string[] {
  return [
    row.termLabel,
    row.dateLabel,
    row.weekdayLabel,
    row.periodLabel,
    row.programLabel,
    row.unitLabel,
    row.learningLabel,
    row.kindLabel,
    row.note,
  ];
}

function buildColumnWidths(dataRows: string[][]): number[] {
  const allRows = [HEADERS as unknown as string[], ...dataRows];
  const baseWidths = [5, 10, 6, 8, 14, 16, 24, 10, 40];
  return baseWidths.map((base, colIndex) => {
    const contentWch = Math.max(
      ...allRows.map((row) => displayWidthOfText((row[colIndex] ?? '').trim())),
      2
    );
    return Math.min(72, Math.max(base, contentWch + 1.5));
  });
}

function triggerBrowserDownload(buffer: ArrayBuffer, fileName: string): void {
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName.endsWith('.xlsx') ? fileName : `${fileName}.xlsx`;
  anchor.click();
  URL.revokeObjectURL(url);
}

function applyTableCellStyle(cell: ExcelJS.Cell, isHeader: boolean): void {
  cell.border = solidCellBorder();
  if (isHeader) {
    cell.font = { bold: true };
    cell.fill = HEADER_FILL;
    cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
    return;
  }
  cell.alignment = { vertical: 'top', wrapText: true };
}

export async function saveClassRecordMemosAsExcel(params: {
  titleLine: string;
  subtitleLine?: string;
  rows: ClassRecordMemosExportRow[];
  fileName: string;
  sheetName?: string;
}): Promise<void> {
  const { titleLine, subtitleLine, rows, fileName, sheetName = '기록모아보기' } = params;
  if (rows.length === 0) return;

  const dataRows = rows.map((row) => rowToCells(row));
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet(sheetName);

  const columnWidths = buildColumnWidths(dataRows);
  sheet.columns = columnWidths.map((width) => ({ width }));

  sheet.mergeCells(1, 1, 1, COLUMN_COUNT);
  const metaCell = sheet.getCell(1, 1);
  metaCell.value = subtitleLine?.trim() ? `${titleLine}\n${subtitleLine.trim()}` : titleLine;
  metaCell.font = { bold: true, size: 12 };
  metaCell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };

  const headerRow = sheet.getRow(HEADER_ROW_INDEX);
  HEADERS.forEach((header, index) => {
    const cell = headerRow.getCell(index + 1);
    cell.value = header;
    applyTableCellStyle(cell, true);
  });

  dataRows.forEach((cells, rowIndex) => {
    const row = sheet.getRow(DATA_START_ROW_INDEX + rowIndex);
    cells.forEach((value, colIndex) => {
      const cell = row.getCell(colIndex + 1);
      cell.value = value;
      applyTableCellStyle(cell, false);
      if (colIndex === 0 || colIndex === 2 || colIndex === 3 || colIndex === 7) {
        cell.alignment = { horizontal: 'center', vertical: 'top', wrapText: true };
      } else if (colIndex >= 5) {
        cell.alignment = { horizontal: 'left', vertical: 'top', wrapText: true };
      }
    });
  });

  const lastRow = DATA_START_ROW_INDEX + dataRows.length - 1;
  for (let rowIndex = HEADER_ROW_INDEX; rowIndex <= lastRow; rowIndex += 1) {
    for (let colIndex = 1; colIndex <= COLUMN_COUNT; colIndex += 1) {
      const cell = sheet.getCell(rowIndex, colIndex);
      if (!cell.border) {
        applyTableCellStyle(cell, rowIndex === HEADER_ROW_INDEX);
      }
    }
  }

  const buffer = await workbook.xlsx.writeBuffer();
  triggerBrowserDownload(buffer, fileName);
}
